import { EliminationType, Toolset } from "@ai360/core";
import * as actions from "./actions";
import { Set } from "immutable";
import { IActionData, IPayloadData } from "./interfaces";

export enum CounterAction {
    Increment = "Increment",
    Decrement = "Decrement",
}

export enum ProcessGroup {
    Events = "Events",
    Exports = "Exports",
    Fields = "Fields",
    Imports = "Imports",
    Recs = "Recs",
    Layers = "Layers",
    Reports = "Reports",
}

const initialState = {
    activeMapTool: null,
    activeToolset: Toolset.DEFAULT,
    activeType: null,
    areaUnits: [
        {
            fullName: "Acres",
            name: "ac",
        },
    ],
    batchReset: false,
    bufferOptions: {
        areaUnit: "acres",
        lengthUnit: "feet",
        selectedUnit: "distance",
        source: "boundary",
    },
    bufferReady: false,
    chosePortalBoundary: false,
    currentProcessCounts: new Map<string, Set<string>>(
        Object.getOwnPropertyNames(ProcessGroup).map((prop) => [prop, Set<string>()])
    ),
    currentUnmatchedDecrements: new Map<string, Set<string>>(
        Object.getOwnPropertyNames(ProcessGroup).map((prop) => [prop, Set<string>()])
    ),
    currentFieldEdits: {
        changed: false,
        geometries: [],
        loaded: false,
    },
    defaultZoneUserGridSettings: {
        areaDimension: "A",
        height: 330,
        width: 330,
        gridSize: 2.5,
        rotation: 0,
        offsetX: 0,
        offsetY: 0,
    },
    drawToolFlags: {
        canClip: false,
        canClipPending: false,
    },
    generatedShrinkwrappedBoundary: false,
    isProcessing: false,
    isZoneGridModalOpen: false,
    lengthUnits: [
        {
            fullName: "Miles",
            name: "mi",
        },
    ],
    gridRemoveFlag: false,
    toolsetDisabled: false,
    toolsetPayload: {},
    traceFlags: {
        apply: false,
        reset: false,
    },
    visibleToolbarMenuDimensions: null,
    zoneEliminateOptions: {
        eliminationType: EliminationType.LONGEST,
        minimumArea: 0.5,
    },
    zoneGridOrientationSettings: { rotation: 0, offsetX: 0, offsetY: 0 },
    zoneGridStandardUnitSettings: { areaAcres: 2.5 },
    zoneGridOptions: {
        areaAcres: 2.5,
        areaDimension: "A",
        height: 330,
        width: 339,
        gridSize: 2.5,
        rotation: 0,
        offsetX: 0,
        offsetY: 0,
    },
};

const updateProcessAndDecrementSets = (
    processSet: Set<string>,
    unmatchedDecrementsSet: Set<string>,
    counterAction: string,
    keys: string[]
): {
    processSet: Set<string>;
    unmatchedDecrementsSet: Set<string>;
} => {
    const keysSet = Set<string>(keys.map((k) => k.toLowerCase()));
    switch (counterAction) {
        case CounterAction.Increment: {
            const filteredNewKeys = keysSet.subtract(unmatchedDecrementsSet);
            return {
                processSet: processSet.union(filteredNewKeys),
                unmatchedDecrementsSet: unmatchedDecrementsSet.subtract(keys),
            };
        }
        case CounterAction.Decrement: {
            const newUnmatchedDecrements = keysSet.subtract(processSet);
            return {
                processSet: processSet.subtract(keys),
                unmatchedDecrementsSet: unmatchedDecrementsSet.union(newUnmatchedDecrements),
            };
        }
        default:
            console.error(`Unknown counter action: ${counterAction}`);
    }

    return {
        processSet,
        unmatchedDecrementsSet,
    };
};

export function mapToolsReducer(state = initialState, action: IActionData): any {
    switch (action.type) {
        case actions.PROCESS_COUNTER_MESSAGE: {
            let processCounts = state.currentProcessCounts;
            let unmatchedDecrements = state.currentUnmatchedDecrements;
            for (const message of action.payload as IPayloadData[]) {
                const set = processCounts.get(message.processGroup);
                if (set == null) {
                    console.error(`Unknown process group: ${message.processGroup}`);
                    continue;
                }
                const unmatchedDecrementsSet = unmatchedDecrements.get(message.processGroup);
                const newSets = updateProcessAndDecrementSets(
                    set,
                    unmatchedDecrementsSet,
                    message.action,
                    message.keyList
                );
                processCounts = processCounts.set(message.processGroup, newSets.processSet);
                unmatchedDecrements = unmatchedDecrements.set(
                    message.processGroup,
                    newSets.unmatchedDecrementsSet
                );
            }

            return {
                ...state,
                currentProcessCounts: processCounts,
                currentUnmatchedDecrements: unmatchedDecrements,
            };
        }
        case actions.APPLY_TRACE:
        case actions.RESET_TRACE:
            return {
                ...state,
                traceFlags: {
                    ...state.traceFlags,
                    ...action.flags,
                },
            };
        case actions.FETCH_DEFAULT_ZONE_USER_GRID_SETTINGS_SUCCEEDED:
            return {
                ...state,
                defaultZoneUserGridSettings: { ...action.defaultZoneUserGridSettings },
            };
        case actions.SET_BATCH_RESET:
            return {
                ...state,
                batchReset: action.batchReset,
            };
        case actions.SET_BUFFER_OPTIONS:
            return {
                ...state,
                bufferOptions: {
                    ...state.bufferOptions,
                    ...action.options,
                },
            };
        case actions.SET_BUFFER_READY:
            return {
                ...state,
                bufferReady: action.ready,
            };
        case actions.CLEAR_TRACE_FLAGS:
            return {
                ...state,
                traceFlags: {
                    ...initialState.traceFlags,
                },
            };
        case actions.SET_ACTIVE_MAP_TOOL:
            return {
                ...state,
                activeMapTool: action.activeMapTool,
            };
        case actions.SET_ACTIVE_TOOLSET:
            return {
                ...state,
                activeToolset: action.activeToolset,
                toolsetDisabled: action.disabled,
                toolsetPayload: action.payload,
            };
        case actions.SET_ACTIVE_TOOLSET_ONLY:
            return {
                ...state,
                activeToolset: action.activeToolset,
                toolsetDisabled: action.disabled,
            };
        case actions.SET_ACTIVE_TOOLSET_PAYLOAD_ONLY:
            return {
                ...state,
                toolsetPayload: action.payload,
            };
        case actions.SET_ACTIVE_TYPE:
            return {
                ...state,
                activeType: action.activeType,
            };
        case actions.SET_CAN_CLIP:
        case actions.SET_CAN_CLIP_PENDING: {
            const { canClip, canClipPending } = action;
            return {
                ...state,
                drawToolFlags: {
                    canClip: canClip != null ? canClip : state.drawToolFlags.canClip,
                    canClipPending:
                        canClipPending != null
                            ? canClipPending
                            : state.drawToolFlags.canClipPending,
                },
            };
        }
        case actions.CLEAR_FIELD_EDIT_GEOMETRIES: {
            const currentFieldEdits = {
                ...state.currentFieldEdits,
                ...action.payload,
            };
            currentFieldEdits.geometries = null;
            return {
                ...state,
                currentFieldEdits,
            };
        }
        case actions.SET_CHOSE_PORTAL_BOUNDARY:
            return {
                ...state,
                chosePortalBoundary: action.chosePortalBoundary,
            };
        case actions.SET_FIELD_EDIT_GEOMETRIES:
        case actions.SET_FIELD_EDITS_CHANGED:
        case actions.SET_FIELD_EDITS_LOADED: {
            const currentFieldEdits = {
                ...state.currentFieldEdits,
                ...action.payload,
            };
            currentFieldEdits.geometries = [...currentFieldEdits.geometries];
            return {
                ...state,
                currentFieldEdits,
            };
        }
        case actions.GENERATE_PROPOSED_BOUNDARY_SUCCESS:
            return {
                ...state,
                proposedBoundary: action.proposedBoundary,
                generatedShrinkwrappedBoundary: true,
            };
        case actions.SET_GENERATED_SHRINKWRAPPED_BOUNDARY:
            return {
                ...state,
                generatedShrinkwrappedBoundary: false,
            };
        case actions.SET_IS_ZONE_ELIMINATE_MODAL_OPEN:
            return {
                ...state,
                isZoneEliminateModalOpen: action.isZoneEliminateModalOpen,
            };
        case actions.SET_IS_ZONE_GRID_MODAL_OPEN:
            return {
                ...state,
                isZoneGridModalOpen: action.isZoneGridModalOpen,
            };
        case actions.SET_JD_FALLBACK_FIELD_EDIT_GEOMETRIES:
            return {
                ...state,
                jdFallbackFieldEditGeometries: action.payload,
            };
        case actions.SET_USER_JD_PORTAL_ACCESS:
            return {
                ...state,
                userHasJdPortalAccess: action.userHasJdPortalAccess,
            };
        case actions.SET_REMOVE_GRID_FLAG:
            return {
                ...state,
                gridRemoveFlag: action.gridRemoveFlag,
            };
        case actions.SET_TOOLS_PROCESSING:
            return Object.freeze({
                ...state,
                isProcessing: action.isProcessing,
            });
        case actions.SET_TOOLSET_DISABLED:
            return {
                ...state,
                toolsetDisabled: action.disabled,
            };
        case actions.SET_VISIBLE_TOOLBAR_MENU_DIMENSIONS:
            return {
                ...state,
                visibleToolbarMenuDimensions: action.dimensions,
            };
        case actions.SET_ZONE_ELIMINATE_OPTIONS:
            return {
                ...state,
                zoneEliminateOptions: { ...action.zoneEliminateOptions },
            };
        case actions.SET_ZONE_GRID_ORIENTATION_SETTINGS: {
            const { zoneGridOrientationSettings } = action;
            return Object.freeze({
                ...state,
                zoneGridOrientationSettings,
            });
        }
        case actions.SET_ZONE_GRID_STANDARD_UNIT_SETTINGS: {
            const { zoneGridStandardUnitSettings } = action;
            return Object.freeze({
                ...state,
                zoneGridStandardUnitSettings,
            });
        }
        case actions.UPDATE_ZONE_GRID_OPTIONS: {
            const { zoneGridOptions } = action;
            return Object.freeze({
                ...state,
                zoneGridOptions: {
                    ...state.zoneGridOptions,
                    ...zoneGridOptions,
                },
            });
        }
        // FIXME: use ~/core/units
        case actions.UNITS_FETCH_FAILED:
            console.error("Failed to get area and length units for user.");
            return state;
        case actions.UNITS_FETCH_SUCCEEDED:
            return {
                ...state,
                areaUnits: action.areaUnits,
                lengthUnits: action.lengthUnits,
            };
        default:
            return state;
    }
}
